import { useState, useMemo } from "react";
import { AppContainer } from "@/components/layout/app-container";
import { NotificationHeader } from "@/components/notificacoes/notification-header";
import { NotificationTable } from "@/components/notificacoes/notification-table";
import { NotificationPagination } from "@/components/notificacoes/notification-pagination";
import { NotificationFilterDialog } from "@/components/notificacoes/notification-filter-dialog";
import { NotificationQuickViewModal } from "@/components/notificacoes/notification-quick-view-modal";
import type { INotification, INotificationFilters } from "@/interfaces/notification.interface";

// Mock data - will be replaced with real API data
const mockNotifications: INotification[] = [
  {
    id: "1",
    date: "2024-01-15",
    type: "Alerta",
    message: "Prazo de entrega do projeto se aproxima",
    status: "Pendente",
    read: false,
    createdAt: "2024-01-15T10:30:00Z",
  },
  {
    id: "2",
    date: "2024-01-14",
    type: "Mensagem",
    message: "Nova mensagem do produtor executivo",
    status: "Lida",
    read: true,
    createdAt: "2024-01-14T14:20:00Z",
  },
  {
    id: "3",
    date: "2024-01-14",
    type: "Pagamento",
    message: "Pagamento processado com sucesso",
    status: "Lida",
    read: true,
    createdAt: "2024-01-14T09:15:00Z",
  },
  {
    id: "4",
    date: "2024-01-13",
    type: "Alerta",
    message: "Documentação pendente de aprovação",
    status: "Pendente",
    read: false,
    createdAt: "2024-01-13T16:45:00Z",
  },
  {
    id: "5",
    date: "2024-01-12",
    type: "Mensagem",
    message: "Comentário adicionado ao seu projeto",
    status: "Lida",
    read: true,
    createdAt: "2024-01-12T11:30:00Z",
  },
  {
    id: "6",
    date: "2024-01-11",
    type: "Pagamento",
    message: "Solicitação de pagamento aprovada",
    status: "Pendente",
    read: false,
    createdAt: "2024-01-11T15:00:00Z",
  },
  {
    id: "7",
    date: "2024-01-10",
    type: "Alerta",
    message: "Orçamento próximo do limite",
    status: "Lida",
    read: true,
    createdAt: "2024-01-10T13:20:00Z",
  },
  {
    id: "8",
    date: "2024-01-09",
    type: "Mensagem",
    message: "Nova atualização no contrato",
    status: "Lida",
    read: true,
    createdAt: "2024-01-09T10:10:00Z",
  },
  {
    id: "9",
    date: "2024-01-08",
    type: "Pagamento",
    message: "Nota fiscal rejeitada - verificar dados",
    status: "Pendente",
    read: false,
    createdAt: "2024-01-08T14:30:00Z",
  },
  {
    id: "10",
    date: "2024-01-07",
    type: "Alerta",
    message: "Validação de equipe pendente",
    status: "Lida",
    read: true,
    createdAt: "2024-01-07T09:45:00Z",
  },
  {
    id: "11",
    date: "2024-01-06",
    type: "Mensagem",
    message: "Resposta do auditor recebida",
    status: "Lida",
    read: true,
    createdAt: "2024-01-06T16:00:00Z",
  },
  {
    id: "12",
    date: "2024-01-05",
    type: "Pagamento",
    message: "Extrato bancário disponível",
    status: "Pendente",
    read: false,
    createdAt: "2024-01-05T12:20:00Z",
  },
];

export default function Notificacoes() {
  const [notifications, setNotifications] = useState<INotification[]>(mockNotifications);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<INotificationFilters>({
    type: [],
    status: [],
    dateFrom: "",
    dateTo: "",
  });

  const itemsPerPage = 10;

  // Filter and search notifications
  const filteredNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      const matchesSearch = notification.message
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesType =
        filters.type.length === 0 || filters.type.includes(notification.type);

      const matchesStatus =
        filters.status.length === 0 || filters.status.includes(notification.status);

      const matchesDateFrom =
        !filters.dateFrom || new Date(notification.date) >= new Date(filters.dateFrom);

      const matchesDateTo =
        !filters.dateTo || new Date(notification.date) <= new Date(filters.dateTo);

      return (
        matchesSearch &&
        matchesType &&
        matchesStatus &&
        matchesDateFrom &&
        matchesDateTo
      );
    });
  }, [notifications, searchTerm, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedNotifications = filteredNotifications.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleApplyFilters = (newFilters: INotificationFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const handleView = (id: string) => {
    // Mark notification as read
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true, status: "Lida" } : n))
    );
    // In real app, would open a detail view or navigate
    console.log("View notification:", id);
  };

  const handleEdit = (id: string) => {
    // In real app, would navigate to edit page
    console.log("Edit notification:", id);
  };

  const handleDelete = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const handleClearAll = () => {
    // Mark all as read
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read: true, status: "Lida" }))
    );
    setIsQuickViewOpen(false);
  };

  const handleViewAll = () => {
    setIsQuickViewOpen(false);
    // Already on notifications page, could scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <AppContainer>
      <div className="space-y-6">
        {/* Header Section */}
        <NotificationHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onFilterClick={() => setIsFilterOpen(true)}
          onNewNotification={() => console.log("New notification request")}
        />

        {/* Main Content Card */}
        <div className="bg-[#15203C] rounded-[20px] p-[30px]">
          {/* Purple Divider */}
          <div className="mb-[30px]">
            <div className="bg-[#7846FF] w-[3px] h-[40px] rounded-[10px]" />
          </div>

          {/* Table */}
          <NotificationTable
            notifications={paginatedNotifications}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-[30px]">
              <NotificationPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredNotifications.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Filter Dialog */}
      <NotificationFilterDialog
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />

      {/* Quick View Modal */}
      <NotificationQuickViewModal
        open={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        notifications={notifications}
        onClearAll={handleClearAll}
        onViewAll={handleViewAll}
      />
    </AppContainer>
  );
}
